import React, {useEffect, useState} from 'react';
import {Button, Card, CardContent, Grid, Link, Typography} from '@mui/material';
import {useAuth} from '@saltus/auth';
import {useSnackbar} from 'notistack';

import QRCodeCanvas from 'qrcode.react';
import {useNavigate} from 'react-router-dom';
import GoogleAuthenticator from '../../assets/images/google-authenticator.svg?react';
import MicrosoftAuthenticator from '../../assets/images/microsoft-authenticator.svg?react';
import MfaEnrolmentPanel from '../../components/Mfa/MfaEnrolmentPanel';
import Theme, {themeColors} from '../../theme/Theme';

const classes = {
  appPanel: {
    backgroundColor: '#25425E',
    boxShadow: 'none',
    marginTop: Theme.spacing(2),
  },

  appPanelInner: {
    alignItems: 'center',
    color: themeColors.white,
    display: 'flex',
    justifyContent: 'space-between',
    padding: 15,
    '&:last-child': {
      paddingBottom: 15,
    },
  },

  appPanelName: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: 600,
    gap: Theme.spacing(2),
    justifyContent: 'space-between',
  },

  button: {
    backgroundColor: themeColors.coral,
    border: `2px solid ${themeColors.coral}`,
    color: themeColors.white,
    fontWeight: 400,
    margin: 0,
    padding: '5px 60px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: themeColors.backgroundDark,
      border: `2px solid ${themeColors.backgroundDark}`,
    },
  },

  link: {
    color: themeColors.coral,
    textDecoration: 'underline',
  },

  linkButton: {
    color: themeColors.coral,
    textDecoration: 'underline',
    textTransform: 'none',
  },

  qrCode: {
    margin: '16px auto',
  },

  secretCode: {
    fontSize: '24px',
    margin: '16px auto',
    wordWrap: 'break-word',
  },

  strongText: {
    fontWeight: 600,
  },
};

type Codes = {
  key: string;
  qrCode: string;
} | null;

function MfaEnrolmentAppPage() {
  const {user, setupTOTP} = useAuth();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [codes, setCodes] = useState<Codes>();
  const [view, setView] = useState('app');

  useEffect(() => {
    if (!codes) {
      setupTOTP().then(setCodes);
    }
  }, [user]);

  return (
    <div>
      <MfaEnrolmentPanel
        title="Two-factor authentication"
        subTitle="Authenticator app"
        backTo="/2fa-enrolment"
        button={
          <Button
            sx={classes.button}
            type="button"
            variant="outlined"
            onClick={() => {
              if (view === 'app') {
                setView('qrCode');
              } else {
                navigate('/2fa-enrolment/code/app');
              }
            }}
          >
            Continue
          </Button>
        }
        align={view === 'app' ? 'left' : 'center'}
      >
        {codes && (
          <>
            {view === 'app' && (
              <>
                <Typography variant="body1">
                  Download an authenticator app, then click continue for a code. Here are
                  some recommended apps:
                </Typography>

                <Card sx={classes.appPanel}>
                  <CardContent sx={classes.appPanelInner}>
                    <div style={classes.appPanelName}>
                      <MicrosoftAuthenticator />
                      Microsoft authenticator
                    </div>

                    <Link
                      sx={classes.link}
                      href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                      target="_blank"
                    >
                      Download now
                    </Link>
                  </CardContent>
                </Card>

                <Card sx={classes.appPanel}>
                  <CardContent sx={classes.appPanelInner}>
                    <div style={classes.appPanelName}>
                      <GoogleAuthenticator />
                      Google authenticator
                    </div>

                    <Link
                      sx={classes.link}
                      href="https://support.google.com/accounts/answer/1066447?co=GENIE.Platform%3DiOS&oco=0"
                      target="_blank"
                    >
                      Download now
                    </Link>
                  </CardContent>
                </Card>
              </>
            )}

            {view === 'qrCode' && (
              <>
                <Typography sx={classes.strongText} variant="body1">
                  Please scan the QR code using the app:
                </Typography>

                <div style={classes.qrCode}>
                  <QRCodeCanvas value={codes.qrCode} />
                </div>

                <Button
                  sx={classes.linkButton}
                  type="button"
                  variant="text"
                  onClick={() => {
                    setView('code');
                  }}
                >
                  Can&apos;t scan the QR code?
                </Button>
              </>
            )}

            {view === 'code' && (
              <>
                <Typography sx={classes.strongText} variant="body1">
                  Please enter this secret key instead:
                </Typography>

                <Typography sx={classes.secretCode} variant="body2">
                  {codes.key}
                </Typography>

                <Grid container>
                  <Grid item xs={12}>
                    <Button
                      sx={classes.linkButton}
                      type="button"
                      variant="text"
                      onClick={() => {
                        navigator.clipboard.writeText(codes.key);

                        enqueueSnackbar('Secret key copied to clipboard', {
                          variant: 'success',
                        });
                      }}
                    >
                      Copy code to clipboard
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      sx={classes.linkButton}
                      type="button"
                      variant="text"
                      onClick={() => {
                        setView('qrCode');
                      }}
                    >
                      Show QR code
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </MfaEnrolmentPanel>
    </div>
  );
}

export default MfaEnrolmentAppPage;
